import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Plus, X, MapPin, Share2 } from "lucide-react";
import { toast } from "sonner";

const LS_KEY = "ks_saved_cities";

export function MultiCityPopular() {
  const [params, setParams] = useSearchParams();
  const urlCities = (params.get("cities") || "").split(",").map((s) => s.trim()).filter(Boolean);
  const [savedCities, setSavedCities] = useState(() => {
    if (urlCities.length) return urlCities;
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); } catch { return []; }
  });
  const [available, setAvailable] = useState([]);
  const [data, setData] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [pending, setPending] = useState("");

  const persist = (list) => {
    setSavedCities(list);
    localStorage.setItem(LS_KEY, JSON.stringify(list));
  };

  const loadCityData = async (list) => {
    const out = {};
    await Promise.all(list.map(async (c) => {
      try {
        const { data } = await api.get("/popular", { params: { city: c } });
        out[c] = data;
      } catch (err) { console.error(`popular failed for ${c}:`, err); }
    }));
    setData(out);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/popular");
        setAvailable(data.available_cities || []);
      } catch (err) { console.error("popular init failed:", err); }
      loadCityData(savedCities);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addCity = () => {
    if (!pending) return;
    if (savedCities.includes(pending)) { toast.info("Already added"); return; }
    const next = [...savedCities, pending];
    persist(next);
    loadCityData(next);
    setPending("");
    setAddOpen(false);
    toast.success(`${pending} added`);
  };

  const remove = (c) => {
    const next = savedCities.filter((x) => x !== c);
    persist(next);
    setData((prev) => { const clone = { ...prev }; delete clone[c]; return clone; });
  };

  return (
    <div data-testid="multi-city-popular">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="tick-label">Popular in your saved cities</div>
        <div className="flex gap-2">
          {savedCities.length > 0 && (
            <button
              data-testid="share-cities-btn"
              onClick={async () => {
                const url = `${window.location.origin}/cook?cities=${encodeURIComponent(savedCities.join(","))}`;
                try {
                  await navigator.clipboard.writeText(url);
                  toast.success("Share link copied");
                } catch { toast.error("Copy failed"); }
              }}
              className="btn-outline inline-flex items-center gap-2"
            ><Share2 size={12} /> Share</button>
          )}
          <button data-testid="add-saved-city-btn" onClick={() => setAddOpen(!addOpen)} className="btn-outline inline-flex items-center gap-2">
            <Plus size={12} /> Add city
          </button>
        </div>
      </div>

      {addOpen && (
        <div className="border hairline p-4 mb-4 flex gap-2 items-center flex-wrap">
          <select
            data-testid="saved-city-select"
            value={pending}
            onChange={(e) => setPending(e.target.value)}
            className="border hairline px-3 py-2 focus:outline-none focus:border-[#FF3B30]"
          >
            <option value="">Pick a city…</option>
            {available.filter((c) => !savedCities.includes(c)).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <button data-testid="add-saved-city-confirm" onClick={addCity} className="btn-signal">Add</button>
        </div>
      )}

      {savedCities.length === 0 ? (
        <div className="border hairline p-8 text-center text-neutral-500">
          Save a city to compare markets side by side.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {savedCities.map((city) => (
            <div key={city} data-testid={`city-column-${city}`} className="border hairline p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5 font-display text-lg font-bold">
                  <MapPin size={14} className="text-[#FF3B30]" /> {city}
                </div>
                <button data-testid={`remove-city-${city}`} onClick={() => remove(city)} className="text-neutral-500 hover:text-[#FF3B30]"><X size={14} /></button>
              </div>
              {!data[city] ? (
                <div className="tick-label">Loading…</div>
              ) : data[city].top_kitchens.length === 0 ? (
                <div className="tick-label">No kitchens yet</div>
              ) : (
                <div className="space-y-2">
                  {data[city].top_kitchens.map((k) => (
                    <Link key={k.id} to={`/kitchen/${k.id}`} className="block border hairline p-2.5 hover:border-[#FF3B30]">
                      <div className="flex justify-between items-baseline">
                        <div className="font-semibold text-sm truncate pr-2">{k.name}</div>
                        <div className="font-mono text-sm">${k.hourly_rate}</div>
                      </div>
                      {k.review_count > 0 && (
                        <div className="tick-label">{k.rating.toFixed(1)} · {k.review_count} rev</div>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
